<template>
  <div v-if="currentUser" class="visits-container">
    <div class="page-header">
      <h1>Wizyty klientów</h1>
      <div class="visit-filter-buttons">
        <button
            type="button"
            class="tab-btn tab-btn-open"
            :class="{ active: !showClosed }"
            @click="showClosed = false"
        >
          Otwarte wizyty
        </button>
        <button
            type="button"
            class="tab-btn tab-btn-closed"
            :class="{ active: showClosed }"
            @click="showClosed = true"
        >
          Zamknięte wizyty
        </button>
      </div>
    </div>

    <div class="visits-layout">
      <div class="visits-list-section">
        <SectionCard>
          <template #header>
            Lista wizyt specjalisty
          </template>

          <div class="visits-list" v-if="filteredVisits.length > 0">
            <div
                v-for="visit in filteredVisits"
                :key="visit.id"
                class="visit-card"
                :class="visit.status"
            >
              <div class="visit-card__top">
                <div>
                  <h3>{{ formatDate(visit.date) }} • {{ visit.time || '-' }}</h3>
                  <div class="meta-row">
                    <span class="status-pill" :class="getStatusClass(visit.status)">
                      {{ getStatusLabel(visit.status) }}
                    </span>
                    <span class="meta-chip">ID wizyty: #{{ visit.id }}</span>
                  </div>
                </div>
              </div>

              <div class="visit-card__grid">
                <p class="meta-item">
                  <span class="meta-label">Klient</span>
                  <span class="meta-value">{{ visit.user_id || '-' }}</span>
                </p>
                <p class="meta-item meta-item--wide">
                  <span class="meta-label">Informacje</span>
                  <span class="meta-value">{{ visit.visit_info || 'Brak dodatkowych informacji' }}</span>
                </p>
              </div>

              <div class="visit-card__actions" v-if="visit.status === 'open'">
                <button type="button" class="btn btn-warning" @click="closeAsNoShow(visit)">
                  Zamknij jako nieodbytą
                </button>
                <button type="button" class="btn btn-success" @click="toggleFinishedPanel(visit.id)">
                  Zamknij jako zakończoną
                </button>
              </div>

              <div v-if="openedFinishPanelId === visit.id && visit.status === 'open'" class="finish-panel">
                <label>
                  Link do oceny dla klienta:
                  <input v-model="ratingLinks[visit.id]" type="text" placeholder="https://..." />
                </label>
                <div class="finish-panel__actions">
                  <button type="button" class="btn btn-success" @click="closeAsFinished(visit)">
                    Potwierdź zakończenie
                  </button>
                  <button type="button" class="btn btn-primary" @click="sendRatingLink(visit)">
                    Wyślij link do oceny
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="empty-state">
            {{ isLoading ? 'Ładowanie wizyt...' : 'Brak wizyt do wyświetlenia.' }}
          </div>

          <div class="pagination" v-if="pagination.lastPage > 1">
            <button
                type="button"
                class="btn-page"
                :disabled="pagination.currentPage <= 1 || isLoading"
                @click="getVisits(pagination.currentPage - 1)"
            >
              Poprzednia
            </button>

            <span class="pagination__info">
              Strona {{ pagination.currentPage }} z {{ pagination.lastPage }}
            </span>

            <button
                type="button"
                class="btn-page"
                :disabled="pagination.currentPage >= pagination.lastPage || isLoading"
                @click="getVisits(pagination.currentPage + 1)"
            >
              Następna
            </button>
          </div>
        </SectionCard>
      </div>
    </div>
  </div>
  <div v-else>
    <h4>Ładowanie...</h4>
  </div>
</template>

<script>
import { useMeta } from 'vue-meta';
import axios from 'axios';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';
import SectionCard from '@/components/Cards/SectionCard.vue';

export default {
  name: 'BizProfileVisits',
  setup() {
    useMeta({ title: 'Wizyty klientów' });

    return {
      authStore: useAuthStore(),
      appStore: useAppStore(),
    }
  },
  data() {
    return {
      visits: [],
      showClosed: false,
      openedFinishPanelId: null,
      ratingLinks: {},
      isLoading: false,
      pagination: {
        currentPage: 1,
        perPage: 15,
        lastPage: 1,
        total: 0
      }
    }
  },
  computed: {
    currentUser() {
      return this.authStore.user;
    },
    specialistIds() {
      if (!this.currentUser) {
        return [];
      }

      const ids = [this.currentUser.id];
      if (this.currentUser.main_id) {
        ids.push(this.currentUser.main_id);
      }

      return ids;
    },
    filteredVisits() {
      const requiredStatus = this.showClosed ? 'closed' : 'open';
      return this.visits.filter(item => item.status === requiredStatus);
    }
  },
  mounted() {
    if (!this.currentUser) {
      this.$router.push('/login');
      return;
    }

    if (this.currentUser.role_id !== 2 && this.currentUser.role_id !== 4) {
      this.$router.push('/login');
      return;
    }

    this.getVisits(1);
  },
  methods: {
    getVisits(page = 1) {
      this.isLoading = true;

      axios.get('/CalendarItems', {
        params: {
          page: page,
          per_page: this.pagination.perPage
        }
      }).then(res => {
        const payload = res.data || {};
        const items = Array.isArray(payload.data) ? payload.data : (Array.isArray(payload) ? payload : []);

        this.visits = items.filter(item => {
          return this.specialistIds.includes(item.business_user_id) || this.specialistIds.includes(item.employee_id);
        });

        this.pagination.currentPage = payload.current_page || page;
        this.pagination.perPage = payload.per_page || this.pagination.perPage;
        this.pagination.lastPage = payload.last_page || 1;
        this.pagination.total = payload.total || this.visits.length;
      }).catch(() => {
        this.appStore.setIsAlert('Nie udało się pobrać listy wizyt.');
      }).finally(() => {
        this.isLoading = false;
      });
    },
    getStatusLabel(status) {
      if (status === 'closed') {
        return 'zamknięta';
      }
      if (status === 'open') {
        return 'otwarta';
      }
      return status || 'nieznany';
    },
    getStatusClass(status) {
      if (status === 'closed') {
        return 'status-pill--closed';
      }
      if (status === 'open') {
        return 'status-pill--open';
      }
      return 'status-pill--other';
    },
    formatDate(date) {
      if (!date) {
        return '-';
      }

      return String(date).split('T')[0];
    },
    toggleFinishedPanel(id) {
      this.openedFinishPanelId = this.openedFinishPanelId === id ? null : id;
    },
    closeAsNoShow(visit) {
      this.appStore.setIsAlert(`TODO API: zamknięcie wizyty #${visit.id} jako nieodbytej (klient nie przyszedł).`);
    },
    closeAsFinished(visit) {
      this.appStore.setIsAlert(`TODO API: zamknięcie wizyty #${visit.id} jako zakończonej.`);
    },
    sendRatingLink(visit) {
      const link = this.ratingLinks[visit.id];
      if (!link) {
        this.appStore.setIsAlert('Uzupełnij link do oceny przed wysłaniem.');
        return;
      }

      this.appStore.setIsAlert(`TODO API: wysyłka linku oceny dla wizyty #${visit.id}. Link: ${link}`);
    }
  },
  components: {
    SectionCard
  }
}
</script>

<style scoped lang='scss'>
.visits-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 16px;
  font-family: sans-serif;
  box-sizing: border-box;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e5e7eb;
}

.page-header h1 {
  font-size: 2.8rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.visits-layout {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.visits-list-section {
  flex: 1 1 0;
  min-width: 0;
}

.visit-filter-buttons {
  display: flex;
  gap: 12px;
}

.tab-btn {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #1f2937;
  border-radius: 8px;
  padding: 10px 14px;
  cursor: pointer;
  font-size: 1.3rem;
  font-weight: 600;
  transition: all .15s ease;
}

.tab-btn-open.active {
  background: #f97316;
  color: #fff;
  border-color: #f97316;
}

.tab-btn-closed.active {
  background: #10b981;
  color: #fff;
  border-color: #10b981;
}

.visits-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.visit-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px;
  background: #fff;
}

.visit-card.closed {
  border-color: #b7ead6;
  background: #f6fffb;
}

.visit-card.open {
  border-color: #ffd9bf;
  background: #fffaf6;
}

.visit-card h3 {
  margin: 0;
  font-size: 1.55rem;
  color: #1f2937;
  font-weight: 700;
}

.meta-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.meta-chip {
  display: inline-flex;
  align-items: center;
  border: 1px solid #e5e7eb;
  color: #6b7280;
  border-radius: 999px;
  padding: 2px 9px;
  font-size: 1.2rem;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 1.2rem;
  font-weight: 700;
  border: 1px solid transparent;
}

.status-pill--open {
  color: #9a3412;
  background: #ffedd5;
  border-color: #fdba74;
}

.status-pill--closed {
  color: #065f46;
  background: #d1fae5;
  border-color: #86efac;
}

.status-pill--other {
  color: #374151;
  background: #f3f4f6;
  border-color: #d1d5db;
}

.visit-card__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(160px, 1fr));
  gap: 8px 14px;
  margin-top: 10px;
}

.meta-item {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.meta-item--wide {
  grid-column: 1 / -1;
}

.meta-label {
  font-size: 1.15rem;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: .03em;
}

.meta-value {
  font-size: 1.3rem;
  line-height: 1.35;
  color: #374151;
}

.visit-card__actions,
.finish-panel__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 10px;
}

.finish-panel {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #d1d5db;
}

.finish-panel label {
  font-size: 1.3rem;
  color: #4b5563;
}

.finish-panel input {
  width: 100%;
  margin-top: 6px;
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1.3rem;
}

.btn {
  border: 0;
  border-radius: 8px;
  padding: 8px 12px;
  color: #fff;
  cursor: pointer;
  font-size: 1.3rem;
  font-weight: 600;
}

.btn-warning {
  background: #ef4444;
}

.btn-success {
  background: #16a34a;
}

.btn-primary {
  background: #2563eb;
}

.pagination {
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.pagination__info {
  font-size: 1.3rem;
  color: #6b7280;
}

.btn-page {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  border-radius: 8px;
  padding: 7px 11px;
  cursor: pointer;
  font-size: 1.25rem;
  font-weight: 600;
}

.btn-page:disabled {
  opacity: .5;
  cursor: not-allowed;
}

.empty-state {
  text-align: center;
  padding: 24px;
  color: #9ca3af;
  font-size: 1.4rem;
}
</style>